<template>
  <div class="mt-2 document-info">
    <custom-callout :has-border="false" :has-toggle="false">
      <div class="row col-12">
        <div class="row col-10">
          <div class="row col-12 col-md-6">
            <div class="col-12">
              <span class="data-title d-block border-mobile">Documento:</span>
              <span class="font-16 color-black">
                {{ documentName | empty }}
              </span>
            </div>
          </div>

          <div class="col-12 col-md-6">
            <div v-if="modelDocumentFileName" class="col-12">
              <span class="data-title d-block border-mobile">Archivo:</span>
              <p class="font-16 color-black">
                {{ modelDocumentFileName | empty }}
              </p>
            </div>
          </div>
        </div>

        <div class="col-2 d-flex align-items-center justify-content-end">
          <action-button
            v-if="modelDocumentId"
            class="px-1 mx-1 download-btn"
            i="fas fa-file-download px-2 mx-0"
            variant="success"
            @click="onDownload(modelDocumentId)"
          />
          <action-button
            v-if="
              canReplaceDocument &&
                modelDocumentFileName &&
                showReplaceButton &&
                !showReplaceForm
            "
            i="fas fa-undo-alt"
            variant="outline-success ml-2"
            @click="startReplace"
          >
            Reemplazar
          </action-button>
          <action-button
            v-if="canAddDocument && !modelDocumentId"
            class="upload-file-button"
            i="fas fa-file-plus color-green"
            @click="onAddHandler"
          >
            Presione aquí para anejar documento
          </action-button>
        </div>
      </div>

      <div v-if="comment !== null" class="mt-3 border-top pt-3">
        <div class="col-12">
          <span class="data-title d-block border-mobile">
            Comentario de reemplazo:
          </span>
          <p class="font-16 color-black">
            {{ modelComment | empty }}
          </p>
        </div>
      </div>

      <div v-if="showReplaceForm" class="mt-3 border-top pt-3">
        <div v-if="requiresComment" class="row col-12">
          <div class="col-6">
            <custom-text-area
              v-model="replaceComment"
              custom-error-msg="Favor completar comentario."
              label="Comentario de reemplazo:"
              :maxlength="3000"
              name="comment"
              :rows="1"
            />
          </div>

          <div class="col-4 d-flex align-items-center">
            <action-button
              i="fas fa-file-alt"
              variant="outline-success ml-2"
              @click="$refs.replaceFileInput.click()"
            >
              Documento
            </action-button>
            <input
              ref="replaceFileInput"
              accept=".pdf"
              class="form-control"
              style="display: none"
              type="file"
              @change="onReplaceFileChange"
            />
            <span v-if="replaceFileName" class="ml-2 text-truncate">
              {{ replaceFileName }}
            </span>
          </div>
          <div class="col-2 d-flex align-items-center justify-content-end">
            <action-button
              :disabled="(requiresComment && !replaceComment) || !replaceFile"
              i="fas fa-exchange-alt"
              text="Guardar"
              variant="success ml-2 mr-2"
              @click="confirmReplace"
            />
            <action-button
              class="action-button"
              i="fas fa-times-circle"
              variant="outline-danger"
              @click="cancelReplace"
            >
              Cancelar
            </action-button>
          </div>
        </div>
      </div>

      <input
        :id="`replaceDocumentHidden${documentTypeId}`"
        :ref="`replaceDocumentHidden${documentTypeId}`"
        accept=".pdf"
        style="display: none"
        type="file"
        @change="updateFileChange"
      />
    </custom-callout>
  </div>
</template>

<script>
import CustomCallout from 'custom-callout';
import ActionButton from 'ActionButton';
import CustomTextArea from 'CustomTextArea';
import appServices from '@/modules/app/api.js';
import storeMixin from '@/shared/mixins/storeGenerator';
import alertMixin from '@/mixins/alert.mixin.js';
import { serialize as objectToFormData } from 'object-to-formdata';
import { uploadOrUpdateFile as _uploadOrUpdateFile } from '@/modules/reinbursement/api.js';

export default {
  name: 'DocumentView',
  components: {
    CustomCallout,
    ActionButton,
    CustomTextArea,
  },
  mixins: [alertMixin, storeMixin],
  props: {
    requestId: { type: Number, required: true },
    documentTypeId: { type: Number, required: true },
    documentName: { type: String, default: () => '' },
    documentFileName: { type: String, default: () => '' },
    documentId: { type: String, default: '' },
    showReplaceButton: { type: Boolean, default: true },
    canAddDocument: { type: Boolean, default: false },
    comment: { type: String, default: () => null },
    requiresComment: { type: Boolean, default: true }, // nueva prop
  },
  data() {
    return {
      modelDocumentFileName: null,
      modelDocumentId: null,
      showReplaceForm: false,
      replaceComment: '',
      replaceFile: null,
      replaceFileName: '',
      modelComment: null,
    };
  },
  computed: {
    canReplaceDocument() {
      return this.userHasPermissions(
        'provider.disbursement.documents.replacement'
      );
    },
  },
  mounted() {
    this.modelDocumentId = this.documentId;
    this.modelDocumentFileName = this.documentFileName;
    this.modelComment = this.comment;
  },
  methods: {
    async onDownload(documentId) {
      await appServices.downloadDocument(documentId);
    },
    startReplace() {
      if (this.requiresComment) {
        this.showReplaceForm = true;
      } else {
        this.onReplaceDocument();
      }
    },
    onReplaceFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.replaceFile = file;
        this.replaceFileName = file.name;
      }
    },
    async confirmReplace() {
      if (this.requiresComment && !this.replaceComment) return;
      if (!this.replaceFile) return;

      const payload = objectToFormData(
        {
          requestId: this.requestId,
          documentTypeId: this.documentTypeId,
          file: this.replaceFile,
          comment: this.requiresComment ? this.replaceComment : null,
        },
        { indices: true, nullsAsUndefineds: true }
      );

      await _uploadOrUpdateFile(payload).then(({ data }) => {
        if (data) {
          this.ShowToast(
            '',
            'El documento ha sido reemplazado exitosamente.',
            'success'
          );
          this.modelDocumentFileName = data.documentFilename;
          this.modelDocumentId = data.documentId;
          this.modelComment = this.replaceComment;
          this.cancelReplace();
        }
      });
    },
    cancelReplace() {
      this.replaceFile = null;
      this.replaceFileName = '';
      this.replaceComment = '';
      this.showReplaceForm = false;
    },
    async onReplaceDocument() {
      const id = this.documentTypeId;
      document.getElementById(`replaceDocumentHidden${id}`).click();
    },
    async updateFileChange(e) {
      if (e.target.files && e.target.files.length > 0) {
        const selectedFile = e.target.files[0];
        let payload = objectToFormData(
          {
            requestId: this.requestId,
            documentTypeId: this.documentTypeId,
            file: selectedFile,
          },
          { indices: true, nullsAsUndefineds: true }
        );
        await _uploadOrUpdateFile(payload).then(({ data }) => {
          if (data) {
            this.ShowToast(
              '',
              'El documento ha sido reemplazado exitosamente.',
              'success'
            );
            this.modelDocumentFileName = data.documentFilename;
            this.modelDocumentId = data.documentId;
            this.$emit('on-document-added', data.documentId);
          }
        });
      }
    },
    onAddHandler() {
      const id = this.documentTypeId;
      document.getElementById(`replaceDocumentHidden${id}`).click();
    },
  },
};
</script>

<style scoped lang="scss">
.document-info {
  ::v-deep {
    .download-btn {
      i {
        margin: 0 !important;
      }
    }
  }
}

.upload-file-button__container {
  background-color: #f7f7f7;
  border-radius: 2px;
  padding: 30px 0;
  margin-bottom: 10px;
  display: flex;
  justify-content: center;
}
.upload-file-button {
  border: 1px solid #e2e2e2 !important;
  padding: 5px 40px !important;
  font-size: 16px;
  background: white;
}
.green-icon {
  color: #2db782;
}

@media (max-width: 480px) {
  .custom-class {
    width: auto !important;
  }
}
</style>
